import { useState, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { supabase } from "@/integrations/supabase/client";
import { useIDTypes } from "@/hooks/admin/identity-docs/useIDTypes";
import { DocumentRequirementsList } from "../components/DocumentRequirementsList";
import { VerificationDocumentForm } from "../components/VerificationDocumentForm";
import { DocumentSaveSuccess } from "../components/DocumentSaveSuccess";
import { UploadedDocumentsList } from "../components/UploadedDocumentsList";
import { useDocumentRequirements } from "../hooks/useDocumentRequirements";
import { determineDocumentPurpose } from "../utils/documentTypeUtils";
import { formSchema, FormValues } from "../schema/documentValidationSchema";

interface VerificationStep3Props {
  formData: any;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
}

const VerificationStep3 = ({
  formData,
  setFormData,
}: VerificationStep3Props) => {
  const { idTypes, isLoading } = useIDTypes();
  const [countries, setCountries] = useState<any[]>([]);
  const [verificationRules, setVerificationRules] = useState<any>(null);

  // Form with dynamic sections array
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      sections: [],
    },
  });

  // Use custom hook for document requirements
  const { documentsNeeded } = useDocumentRequirements(
    verificationRules,
    idTypes,
    formData.verificationCountry,
    formData.personalInfo?.residency_status,
    form
  );

  // Load all countries on mount
  useEffect(() => {
    const loadCountries = async () => {
      const { data: countriesData, error } = await supabase
        .from("countries")
        .select("*")
        .order("name");

      if (error) {
        console.error("Error loading countries:", error);
        return;
      }

      setCountries(countriesData || []);
    };

    loadCountries();
  }, []);

  // Load verification rules when verification country changes
  useEffect(() => {
    const loadVerificationRules = async () => {
      if (!formData.verificationCountry) return;

      const { data: rulesData, error } = await supabase
        .from("verification_rules")
        .select("*")
        .eq("country_code", formData.verificationCountry)
        .single();

      if (error) {
        console.error("Error loading verification rules:", error);
        return;
      }

      setVerificationRules(
        rulesData || {
          requires_primary_document: true,
          requires_residency_document: false,
          expatriate_requires_passport: true,
          expatriate_requires_residency: true,
        }
      );
    };

    loadVerificationRules();
  }, [formData.verificationCountry]);

  // Handle document type change
  const handleDocumentTypeChange = (value: string, sectionIndex: number) => {
    const countryCode = form.getValues(`sections.${sectionIndex}.country_code`);
    const purpose = determineDocumentPurpose(
      value,
      countryCode,
      sectionIndex,
      idTypes,
      formData.verificationCountry,
      formData.personalInfo?.residency_status,
      documentsNeeded
    );
    form.setValue(
      `sections.${sectionIndex}.verification_purpose`,
      purpose as any
    );
  };

  // Submit all documents
  const handleSubmitDocuments = (data: FormValues) => {
    // Update the documents in the main form data
    const formattedDocuments = data.sections.map((section) => ({
      ...section,
      verification_country: formData.verificationCountry,
    }));

    setFormData((prev: any) => ({
      ...prev,
      documents: formattedDocuments,
    }));
  };

  // Determine if all required documents are fulfilled
  const allRequirementsFulfilled =
    documentsNeeded.length > 0 && documentsNeeded.every((doc) => doc.fulfilled);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold mb-2">Document Upload</h2>
        <p className="text-muted-foreground mb-6">
          Please upload identity documents for verification. You may need to
          provide multiple documents based on your residency status.
        </p>
      </div>

      {/* Document Requirements List */}
      <DocumentRequirementsList documentsNeeded={documentsNeeded} />

      {/* Document Form */}
      <VerificationDocumentForm
        form={form}
        countries={countries}
        idTypes={idTypes}
        onDocumentTypeChange={handleDocumentTypeChange}
        documentsNeeded={documentsNeeded}
        sections={form.getValues("sections") || []}
        handleSubmitDocuments={handleSubmitDocuments}
        verificationCountry={formData.verificationCountry}
        residencyStatus={formData.personalInfo?.residency_status}
      />

      {/* Document Save Success */}
      <DocumentSaveSuccess documents={formData.documents} />

      {/* Uploaded Documents List */}
      <UploadedDocumentsList
        documents={formData.documents}
        idTypes={idTypes}
        countries={countries}
      />
    </div>
  );
};

export default VerificationStep3;
